import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  ListItemButton,
  AppBar,
  Toolbar,
  Button
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Subject as SubjectIcon,
  School as SchoolIcon,
  CalendarToday as CalendarIcon,
  Support as SupportIcon,
  Settings as SettingsIcon,
  Payment as PaymentIcon,
  Add as AddIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ContactSupport,
  Person as PersonIcon,
  VpnKey as VpnKeyIcon,
  ExitToApp,
  Menu as MenuIcon,
  Speed as SpeedIcon,
  QuestionMark as QuestionMarkIcon,
  Psychology as PsychologyIcon,
  MenuBook as MenuBookIcon
} from '@mui/icons-material';
import {
  handleProfile,
  handleChangePassword,
  handleLogout
} from '../common/GeneralnNagivationHandler';
import SupportDialog from '../SupportDialog';

const drawerWidth = 280;
const collapsedDrawerWidth = 72;

const AdminLayout = ({ 
  children,
  open, 
  onClose, 
  collapsed, 
  onToggleCollapse
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);

  // Admin navigation menu items
  const menuItems = [
    {
      group: 'Main',
      items: [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard', ariaLabel: 'Navigate to Admin Dashboard' }
      ]
    },
    {
      group: 'User Management',
      items: [
        { text: 'Manage Users', icon: <PeopleIcon />, path: '/admin/users', ariaLabel: 'Manage System Users' },
        { text: 'Status Management', icon: <VpnKeyIcon />, path: '/admin/statuses', ariaLabel: 'Manage Status Values' }
      ]
    },
    {
      group: 'Content Management',
      items: [
        { text: 'Boards', icon: <SchoolIcon />, path: '/admin/boards', ariaLabel: 'Manage Educational Boards' },
        { text: 'Academic Years', icon: <CalendarIcon />, path: '/admin/academic-years', ariaLabel: 'Manage Academic Years' },
        { text: 'Subjects', icon: <SubjectIcon />, path: '/admin/subjects', ariaLabel: 'Manage Subjects' },
        { text: 'Manage Curriculum', icon: <SubjectIcon />, path: '/admin/subject-topics', ariaLabel: 'Manage Curriculum' },
        { text: 'Semester Templates', icon: <MenuBookIcon />, path: '/admin/semester-templates', ariaLabel: 'Semester Templates' },
        { text: 'Question Difficulty', icon: <AddIcon />, path: '/admin/difficulties', ariaLabel: 'Manage Question Difficulties' },
        { text: 'Meta Tags', icon: <AddIcon />, path: '/admin/meta-tags', ariaLabel: 'Manage Meta Tags' },
        { text: 'Exam Modes', icon: <SchoolIcon />, path: '/admin/exam-modes', ariaLabel: 'Manage Exam Modes' }
      ]
    },
    {
      group: 'Question Management',
      items: [
        { text: 'Question Bank', icon: <QuestionMarkIcon />, path: '/admin/questions', ariaLabel: 'Manage All Questions' },
        { text: 'Question Analytics', icon: <SpeedIcon />, path: '/admin/question-analytics', ariaLabel: 'View Question Analytics' }
      ]
    },
    {
      group: 'Support & Features',
      items: [
        { text: 'Support Requests', icon: <SupportIcon />, path: '/admin/support-requests', ariaLabel: 'View Support Requests' },
        { text: 'Features', icon: <SettingsIcon />, path: '/admin/features', ariaLabel: 'Manage System Features' },
        { text: 'Feature Access', icon: <SecurityIcon />, path: '/admin/feature-access', ariaLabel: 'Manage Feature Access' }
      ]
    },
    {
      group: 'Billing & Subscriptions',
      items: [
        { text: 'Pricing Plans', icon: <PaymentIcon />, path: '/admin/pricing-plans', ariaLabel: 'Manage Pricing Plans' },
        { text: 'Add On Catalog', icon: <AddIcon />, path: '/admin/addon-catalog', ariaLabel: 'Manage Add-on Catalog' },
        { text: 'User Subscriptions', icon: <BusinessIcon />, path: '/admin/user-subscriptions', ariaLabel: 'Manage User Subscriptions' }
      ]
    },
    {
      group: 'System',
      items: [
        { text: 'Tenants', icon: <BusinessIcon />, path: '/admin/tenants', ariaLabel: 'Manage Tenants' },
        { text: 'Referrals', icon: <PeopleIcon />, path: '/admin/referrals', ariaLabel: 'Manage Referrals and Promotions' }
      ]
    },
    {
      group: 'Throttle',
      items: [
        { text: 'Throttle Config', icon: <SpeedIcon />, path: '/admin/throttle-config', ariaLabel: 'Configure Rate Limiting' }
      ]
    },
    {
      group: 'AI',
      items: [
        { text: 'AI Prompts', icon: <PsychologyIcon />, path: '/admin/ai-prompts', ariaLabel: 'Manage AI Prompts' }
      ]
    }
  ];

  const handleProfileClick = () => {
    handleProfile(navigate, () => setAnchorEl(null));
    setAnchorEl(null);
  };

  const handleChangePasswordClick = () => {
    handleChangePassword(navigate, () => setAnchorEl(null));
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleLogout(logout, navigate, () => setAnchorEl(null));
    setAnchorEl(null);
  };

  const handleSupportOpen = () => {
    setSupportOpen(true);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const renderMenuItem = (item) => {
    const active = isActive(item.path);
    
    const handleClick = () => {
      navigate(item.path);
      // Close mobile menu if on mobile
      if (isMobile) {
        onClose();
      }
    };
    
    const menuItem = (
      <ListItemButton
        key={item.text}
        onClick={handleClick}
        selected={active}
        sx={{
          borderRadius: 1,
          mx: 1,
          mb: 0.5,
          minHeight: 48,
          position: 'relative',
          color: 'rgba(255, 255, 255, 0.7)',
          '&.Mui-selected': {
            backgroundColor: 'rgba(25, 118, 210, 0.16)',
            color: '#FFFFFF',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.24)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 4,
              backgroundColor: '#1976d2',
              borderRadius: '0 2px 2px 0'
            }
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            color: '#FFFFFF',
          }
        }}
        aria-label={item.ariaLabel}
      >
        <ListItemIcon 
          sx={{ 
            minWidth: collapsed ? 40 : 48,
            color: active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)'
          }}
        >
          {item.icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText 
            primary={item.text}
            primaryTypographyProps={{
              fontWeight: active ? 600 : 400,
              color: active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)'
            }}
          />
        )}
      </ListItemButton>
    );

    // Add tooltip when collapsed
    if (collapsed) {
      return (
        <Tooltip 
          key={item.text}
          title={item.text} 
          placement="right"
          arrow
        >
          {menuItem}
        </Tooltip>
      );
    }

    return menuItem;
  };

  const renderMenuGroup = (group) => {
    if (collapsed) {
      return group.items.map(renderMenuItem);
    }

    return (
      <Box key={group.group}>
        <Typography
          variant="caption"
          sx={{
            px: 3,
            py: 1,
            color: 'rgba(255, 255, 255, 0.5)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontSize: '0.75rem'
          }}
        >
          {group.group}
        </Typography>
        {group.items.map(renderMenuItem)}
      </Box>
    );
  };

  const getPageTitle = () => {
    const pathMap = {
      '/admin/dashboard': 'Admin Dashboard',
      '/admin/users': 'Manage Users',
      '/admin/statuses': 'Status Management',
      '/admin/subjects': 'Subjects',
      '/admin/subject-topics': 'Manage Curriculum',
      '/admin/semester-templates': 'Semester Templates',
      '/admin/boards': 'Boards',
      '/admin/academic-years': 'Academic Years',
      '/admin/exam-modes': 'Exam Modes',
      '/admin/support-requests': 'Support Requests',
      '/admin/features': 'Features',
      '/admin/feature-access': 'Feature Access',
      '/admin/pricing-plans': 'Pricing Plans',
      '/admin/addon-catalog': 'Add On Catalog',
      '/admin/user-subscriptions': 'User Subscriptions',
      '/admin/tenants': 'Tenants',
      '/admin/referrals': 'Referrals & Promotions',
      '/admin/throttle-config': 'Throttle Configuration',
      '/admin/ai-prompts': 'AI Prompts Management',
      '/admin/questions': 'Question Bank',
      '/admin/question-analytics': 'Question Analytics'
    };

    return pathMap[location.pathname] || 'Admin Dashboard';
  };

  const drawerContent = (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100vh',
      width: '100%',
      overflow: 'hidden',
      margin: 0,
      padding: 0,
      boxSizing: 'border-box',
      position: 'relative'
    }}>
      {/* Header - Fixed at top */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        px: 2,
        py: 1.5,
        minHeight: 64,
        height: 64,
        flexShrink: 0,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        margin: 0,
        overflow: 'hidden',
        border: 'none',
        '&::before': { display: 'none' },
        '&::after': { display: 'none' }
      }}>
        {!collapsed && (
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600, 
              color: '#FFFFFF',
              flexShrink: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              margin: 0
            }}
          >
            Admin Panel
          </Typography>
        )}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          flexShrink: 0,
          overflow: 'hidden'
        }}>
          {/* Mobile close button - only show on mobile */}
          {isMobile && (
            <IconButton
              onClick={onClose}
              sx={{ 
                color: '#FFFFFF',
                '&:hover': { color: 'rgba(255, 255, 255, 0.8)' },
                flexShrink: 0,
                margin: 0
              }}
              aria-label="Close mobile menu"
            >
              <ChevronLeftIcon />
            </IconButton>
          )}
          {/* Desktop collapse toggle - only show on desktop */}
          {!isMobile && (
            <IconButton
              onClick={onToggleCollapse}
              sx={{ 
                color: '#FFFFFF',
                '&:hover': { color: 'rgba(255, 255, 255, 0.8)' },
                flexShrink: 0,
                margin: 0
              }}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Navigation Menu - Scrollable content area */}
      <Box sx={{ 
        flex: 1,
        minHeight: 0, // Critical: prevents flex overflow bug
        overflowY: 'auto',
        overflowX: 'hidden',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        margin: 0,
        padding: 0,
        '&::-webkit-scrollbar': {
          width: '8px'
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(255, 255, 255, 0.1)'
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(255, 255, 255, 0.3)',
          borderRadius: '4px',
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.5)'
          }
        }
      }}>
        <List sx={{ 
          py: 1,
          px: 0,
          margin: 0,
          '& .MuiListItemButton-root': {
            minHeight: isMobile ? 56 : 48,
            mx: isMobile ? 0.5 : 1,
            mb: isMobile ? 0.25 : 0.5,
          }
        }}>
          {menuItems.map(renderMenuGroup)}
        </List>
      </Box>

      {/* Footer - Fixed at bottom */}
      {!collapsed && (
        <Box sx={{ 
          p: 2, 
          flexShrink: 0,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          margin: 0,
          overflow: 'hidden',
          border: 'none',
          '&::before': { display: 'none' },
          '&::after': { display: 'none' }
        }}>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', width: '100%', overflow: 'hidden' }}>
      {/* Sidebar */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={isMobile ? open : true}
        onClose={onClose}
        sx={{
          width: collapsed ? collapsedDrawerWidth : drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: collapsed ? collapsedDrawerWidth : drawerWidth,
            boxSizing: 'border-box',
            border: 'none',
            boxShadow: '0 0 20px rgba(0,0,0,0.1)',
            backgroundColor: '#1a237e',
            color: '#FFFFFF',
            transition: theme.transitions.create(['width', 'transform'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard,
            }),
            position: isMobile ? 'fixed' : 'relative',
            height: '100vh',
            maxHeight: '100vh',
            zIndex: isMobile ? theme.zIndex.modal : theme.zIndex.drawer,
            transform: isMobile && !open ? 'translateX(-100%)' : 'translateX(0)',
            touchAction: 'pan-y',
            top: 0,
            left: 0,
            overflow: 'hidden',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column'
          }
        }}
        ModalProps={{
          keepMounted: true, // Better mobile performance
          // Add swipe to close functionality
          onTouchStart: (e) => {
            if (isMobile) {
              const touch = e.touches[0];
              if (touch.clientX > 50) { // If touch starts in the middle/right of screen
                onClose();
              }
            }
          },
          // Improve focus management
          disableAutoFocus: false,
          disableEnforceFocus: false,
          disableRestoreFocus: false
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: theme.zIndex.modal - 1,
            transition: 'opacity 0.3s ease-in-out'
          }
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box sx={{ 
        flex: 1, // Use flex: 1 instead of flexGrow: 1 to fill remaining space
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
        minWidth: 0, // Prevent flexbox overflow
        position: 'relative',
        mt: 0, // Ensure no top margin
        pt: 0,  // Ensure no top padding
        ml: 0, // Remove left margin - flexbox will handle spacing
        width: 0 // Help flexbox calculate correctly
      }}>
        {/* Top Bar */}
        <AppBar 
          position="static" 
          elevation={0}
          sx={{ 
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            minHeight: 64, // Ensure consistent height
            mt: 0, // Ensure no top margin
            pt: 0  // Ensure no top padding
          }}
        >
          <Toolbar sx={{ 
            justifyContent: 'space-between', 
            minHeight: '64px !important',
            py: 0, // Remove vertical padding
            px: 2,  // Keep horizontal padding
            alignItems: 'center' // Ensure vertical centering
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Mobile menu toggle - only show on mobile */}
              <IconButton
                edge="start"
                onClick={onClose}
                sx={{ 
                  mr: 2,
                  display: { xs: 'flex', sm: 'none' }, // Only show on mobile
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                  // Add visual indicator for mobile menu
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 0.5
                }}
                aria-label="Toggle mobile menu"
              >
                <MenuIcon />
              </IconButton>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '1.1rem', sm: '1.25rem' }, // Smaller on mobile
                  lineHeight: 1.2
                }}
              >
                {getPageTitle()}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContactSupport />}
                onClick={handleSupportOpen}
                sx={{ display: { xs: 'none', sm: 'flex' } }}
              >
                Need Help?
              </Button>

              <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ 
                  width: 40, 
                  height: 40,
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                {user?.first_name?.charAt(0) || user?.email?.charAt(0) || 'A'}
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        {/* User Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 200,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }
          }}
        >
          <MenuItem onClick={handleProfileClick}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="My Profile" />
          </MenuItem>
          
          <MenuItem onClick={handleChangePasswordClick}>
            <ListItemIcon>
              <VpnKeyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Change Password" />
          </MenuItem>
          
          <Divider />
          
          <MenuItem onClick={handleLogoutClick}>
            <ListItemIcon>
              <ExitToApp fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </MenuItem>
          
          <Divider />
          
          <MenuItem disabled>
            <ListItemText 
              primary={user?.email || 'No email'} 
              primaryTypographyProps={{ 
                variant: 'caption', 
                color: 'text.secondary',
                sx: { fontStyle: 'italic' }
              }} 
            />
          </MenuItem>
        </Menu>

        {/* Page Content */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          bgcolor: 'background.default',
          width: '100%',
          height: '100%',
          minWidth: 0, // Prevent flexbox overflow
          display: 'flex',
          flexDirection: 'column'
        }}>
          {children}
        </Box>
      </Box>

      {/* Support Dialog */}
      <SupportDialog 
        open={supportOpen} 
        onClose={() => setSupportOpen(false)} 
      />
    </Box>
  );
};

export default AdminLayout; 